// Cache utility for storing API responses in localStorage
// Provides resilient data access when API is slow or unavailable
//
// Entries are keyed by the signed-in user (see userScope.ts): with no known
// user nothing is read or written, and one user's entries are never served
// to another. "Cached mode" (read-only while the API is down) lives in
// memory only, so a new session never starts read-only. The browser being
// offline (connectivity.ts) makes the app read-only too; isReadOnly() is
// either.

import { logger } from "./logger";
import { currentSubject, scopeId } from "./userScope";
import {
  browserOnline,
  isOfflineError,
  onBrowserConnectivity,
} from "./connectivity";

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  version: string;
}

interface CacheConfig {
  ttl?: number; // Time to live in milliseconds
  version?: string; // Cache version for invalidation
}

export interface CacheStatus {
  isCachedMode: boolean;
  lastApiSuccess: number | null;
  failedRequests: number;
  /** The browser says it has no network (navigator.onLine, offline event). */
  browserOffline: boolean;
}

const CACHE_PREFIX = "linktapestry_cache_";
// Per-user entries live under `${CACHE_PREFIX}u:<user>:<key>`.
const USER_PREFIX = `${CACHE_PREFIX}u:`;
// Cached-mode status used to be persisted here; it is now in memory only and
// the old key is removed at startup.
const LEGACY_STATUS_KEY = "linktapestry_cache_status";
const DEFAULT_TTL = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Stamped on every entry; an entry written by another build is dropped on
 * read. Derived from the build's bundle hash, so a deploy invalidates entries
 * whose shape the new UI may not understand.
 */
export const CACHE_VERSION: string = (() => {
  try {
    return `build-${__BUILD_INFO__.hash}`;
  } catch {
    return "build-unknown";
  }
})();

/** Cached mode needs this many consecutive outage failures... */
const CACHED_MODE_FAILURES = 3;
/** ...or failures spanning this long with no success in between. */
const CACHED_MODE_OUTAGE_MS = 30_000;

/**
 * True for a failure that means the API is unreachable or broken: a network
 * error, a timeout, or a 5xx. A 4xx (a 404 for one record, a validation
 * error) says the API is up, so it never counts towards cached mode.
 */
export function isOutageError(error: unknown): boolean {
  if (!error) return false;
  const e = error as { status?: unknown; timeout?: unknown; message?: unknown };
  if (e.timeout === true) return true;
  if (typeof e.status === "number") return e.status >= 500;
  if (error instanceof TypeError) return true; // fetch's network failure
  const message = typeof e.message === "string" ? e.message : "";
  return /API Error 5\d\d\b/.test(message);
}

const EMPTY_STATUS: Omit<CacheStatus, "browserOffline"> = {
  isCachedMode: false,
  lastApiSuccess: null,
  failedRequests: 0,
};

/**
 * Snapshot of every key currently in localStorage. Uses the portable
 * `key(i)` iteration rather than `allStorageKeys()` — the latter
 * works in browsers but is not guaranteed by the Storage spec and jsdom does
 * not populate it. Returning a snapshot array makes it safe to remove entries
 * while iterating the result.
 */
function allStorageKeys(): string[] {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key !== null) keys.push(key);
  }
  return keys;
}

export class CacheManager {
  private static instance: CacheManager;
  private status: CacheStatus = {
    ...EMPTY_STATUS,
    browserOffline: !browserOnline(),
  };
  private firstFailureAt: number | null = null;
  private statusChangeListeners: Set<(status: CacheStatus) => void> = new Set();
  // Whose entries are read and written; null means none are.
  private user: string | null;

  private constructor() {
    try {
      localStorage.removeItem(LEGACY_STATUS_KEY);
    } catch {
      // ignore
    }
    this.cleanExpiredEntries();
    // The session has already taken any sign-in landing in the URL hash, so
    // this is the user now signed in, never the previous one.
    this.user = currentSubject();
    onBrowserConnectivity((online) =>
      this.updateStatus({ browserOffline: !online }),
    );
  }

  /** The user whose entries are in use, or null. */
  getUser(): string | null {
    return this.user;
  }

  /**
   * Switch whose entries are read and written. Requests started for the
   * previous user are discarded when they land (see fetchAndStore).
   */
  setUser(user: string | null): void {
    if (user === this.user) return;
    this.user = user;
    epoch++;
    inFlight.clear();
  }

  static getInstance(): CacheManager {
    if (!CacheManager.instance) {
      CacheManager.instance = new CacheManager();
    }
    return CacheManager.instance;
  }

  /**
   * Store data in cache
   */
  set<T>(key: string, data: T, config?: CacheConfig): void {
    const cacheKey = this.getCacheKey(key);
    if (!cacheKey) return;
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      version: config?.version || CACHE_VERSION,
    };
    const serialized = JSON.stringify(entry);
    try {
      localStorage.setItem(cacheKey, serialized);
    } catch (error) {
      // Quota exceeded — evict oldest 25% then retry once. If retry also
      // fails, drop this write rather than silently leaving the entry absent
      // (silent drops break cache-first hydration).
      logger.warn("Cache storage failed, attempting eviction", {
        key,
        sizeKB: Math.round(serialized.length / 1024),
        error: (error as Error).message,
      });
      this.handleStorageError();
      try {
        localStorage.setItem(cacheKey, serialized);
      } catch (retryError) {
        logger.error("Cache storage failed after eviction", retryError, {
          key,
          sizeKB: Math.round(serialized.length / 1024),
        });
      }
    }
  }

  /**
   * Retrieve data from cache
   */
  get<T>(key: string, config?: CacheConfig): T | null {
    try {
      const cacheKey = this.getCacheKey(key);
      if (!cacheKey) return null;
      const stored = localStorage.getItem(cacheKey);

      if (!stored) {
        return null;
      }

      const entry: CacheEntry<T> = JSON.parse(stored);

      // Check version
      if (entry.version !== (config?.version || CACHE_VERSION)) {
        this.remove(key);
        return null;
      }

      // Check TTL. An expired entry is reported as a miss but is NOT
      // removed: withCache() reads it back with ttl: Infinity to serve it as
      // stale-while-revalidate data, and the Redux slices hydrate from it on
      // the next page load. Deleting here used to wipe the persisted cache on
      // every expired read, so a tab closed before the refetch finished left
      // the next load with nothing to hydrate.
      const ttl = config?.ttl ?? DEFAULT_TTL;
      const age = Date.now() - entry.timestamp;

      if (age > ttl) {
        return null;
      }

      return entry.data;
    } catch (error) {
      logger.error("Cache retrieval failed", error, { key });
      return null;
    }
  }

  /**
   * Check if cache has valid data
   */
  has(key: string, config?: CacheConfig): boolean {
    return this.get(key, config) !== null;
  }

  /**
   * Remove specific cache entry
   */
  remove(key: string): void {
    // A request already in flight for this key predates the change that made
    // the caller invalidate it: never let it write back.
    invalidateKey(key);
    try {
      const cacheKey = this.getCacheKey(key);
      if (cacheKey) localStorage.removeItem(cacheKey);
    } catch (error) {
      logger.error("Cache removal failed", error, { key });
    }
  }

  /**
   * Remove all cache entries matching a prefix
   */
  removeByPrefix(prefix: string): void {
    invalidatePrefix(prefix);
    try {
      const fullPrefix = this.getCacheKey(prefix);
      if (!fullPrefix) return;
      allStorageKeys().forEach((key) => {
        if (key.startsWith(fullPrefix)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      logger.error("Cache prefix removal failed", error, { prefix });
    }
  }

  /**
   * Clear all cache entries, every user's.
   */
  clear(): void {
    invalidatePrefix("");
    try {
      const keys = allStorageKeys();
      keys.forEach((key) => {
        if (key.startsWith(CACHE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
      logger.info("Cache cleared");
    } catch (error) {
      logger.error("Cache clear failed", error);
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): { entries: number; size: number } {
    let entries = 0;
    let size = 0;

    allStorageKeys().forEach((key) => {
      if (key.startsWith(CACHE_PREFIX)) {
        entries++;
        const item = localStorage.getItem(key);
        if (item) {
          size += item.length;
        }
      }
    });

    return { entries, size };
  }

  /**
   * Clean expired entries
   */
  private cleanExpiredEntries(): void {
    try {
      const keys = allStorageKeys();
      let cleaned = 0;

      keys.forEach((key) => {
        if (key.startsWith(CACHE_PREFIX)) {
          // Entries from before per-user keys can't be attributed to anyone.
          if (!key.startsWith(USER_PREFIX)) {
            localStorage.removeItem(key);
            cleaned++;
            return;
          }
          const stored = localStorage.getItem(key);
          if (stored) {
            try {
              const entry: CacheEntry<unknown> = JSON.parse(stored);
              const age = Date.now() - entry.timestamp;

              // Remove entries older than 7 days regardless of TTL
              if (age > 7 * 24 * 60 * 60 * 1000) {
                localStorage.removeItem(key);
                cleaned++;
              }
            } catch {
              // Remove corrupted entries
              localStorage.removeItem(key);
              cleaned++;
            }
          }
        }
      });

      if (cleaned > 0) {
        logger.debug(`Cleaned ${cleaned} expired cache entries`);
      }
    } catch (error) {
      logger.error("Cache cleanup failed", error);
    }
  }

  /**
   * Handle storage errors (quota exceeded, etc.)
   */
  private handleStorageError(): void {
    // Try to free up space by removing oldest entries
    try {
      const cacheEntries: Array<{ key: string; timestamp: number }> = [];

      allStorageKeys().forEach((key) => {
        if (key.startsWith(CACHE_PREFIX)) {
          const stored = localStorage.getItem(key);
          if (stored) {
            try {
              const entry = JSON.parse(stored);
              cacheEntries.push({ key, timestamp: entry.timestamp });
            } catch {
              // Remove corrupted entry
              localStorage.removeItem(key);
            }
          }
        }
      });

      // Sort by timestamp (oldest first)
      cacheEntries.sort((a, b) => a.timestamp - b.timestamp);

      // Remove oldest 25% of entries
      const toRemove = Math.ceil(cacheEntries.length * 0.25);
      for (let i = 0; i < toRemove; i++) {
        localStorage.removeItem(cacheEntries[i].key);
      }

      logger.warn(`Freed cache space by removing ${toRemove} old entries`);
    } catch (error) {
      logger.error("Failed to handle cache storage error", error);
    }
  }

  /**
   * The storage key for `key` under the current user, or null with no user.
   */
  private getCacheKey(key: string): string | null {
    if (!this.user) return null;
    return `${USER_PREFIX}${scopeId(this.user)}:${key}`;
  }

  /**
   * Update cache status and notify listeners when it changed.
   */
  private updateStatus(updates: Partial<CacheStatus>): void {
    const next = { ...this.status, ...updates };
    const changed =
      next.isCachedMode !== this.status.isCachedMode ||
      next.failedRequests !== this.status.failedRequests ||
      next.lastApiSuccess !== this.status.lastApiSuccess ||
      next.browserOffline !== this.status.browserOffline;
    this.status = next;
    if (changed) this.notifyStatusListeners();
  }

  /** Back to "online, no failures" (sign-out, user change). */
  resetStatus(): void {
    this.firstFailureAt = null;
    this.updateStatus({ ...EMPTY_STATUS, browserOffline: !browserOnline() });
  }

  /**
   * Notify all status change listeners
   */
  private notifyStatusListeners(): void {
    this.statusChangeListeners.forEach((listener) => {
      try {
        listener(this.status);
      } catch (error) {
        logger.error("Error in cache status listener", error);
      }
    });
  }

  /**
   * Subscribe to cache status changes
   */
  onStatusChange(listener: (status: CacheStatus) => void): () => void {
    this.statusChangeListeners.add(listener);
    // Return unsubscribe function
    return () => {
      this.statusChangeListeners.delete(listener);
    };
  }

  /**
   * Get current cache status
   */
  getStatus(): CacheStatus {
    return { ...this.status };
  }

  /**
   * Record successful API call
   */
  recordApiSuccess(): void {
    this.firstFailureAt = null;
    this.updateStatus({
      isCachedMode: false,
      lastApiSuccess: Date.now(),
      failedRequests: 0,
    });
  }

  /**
   * Record a failed API call. Only outage failures (network, timeout, 5xx)
   * count; cached mode starts after several in a row, or after failures
   * spanning a sustained outage.
   */
  recordApiFailure(error?: unknown): void {
    // A request the client didn't send because the browser is offline says
    // nothing about the API; the browser's state already makes us read-only.
    if (isOfflineError(error) || !isOutageError(error)) return;

    const now = Date.now();
    if (this.firstFailureAt === null) this.firstFailureAt = now;
    const failedRequests = this.status.failedRequests + 1;
    const wasCachedMode = this.status.isCachedMode;

    const shouldEnterCachedMode =
      wasCachedMode ||
      failedRequests >= CACHED_MODE_FAILURES ||
      (failedRequests >= 2 &&
        now - this.firstFailureAt >= CACHED_MODE_OUTAGE_MS);

    this.updateStatus({
      isCachedMode: shouldEnterCachedMode,
      failedRequests,
    });

    if (shouldEnterCachedMode && !wasCachedMode) {
      logger.warn("Entering cached mode - edit/delete functionality disabled", {
        failedRequests,
      });
    }
  }

  /**
   * Check if we're in cached mode
   */
  isCachedMode(): boolean {
    return this.status.isCachedMode;
  }

  /** Offline either way: the browser says so, or the API keeps failing. */
  isReadOnly(): boolean {
    return this.status.isCachedMode || this.status.browserOffline;
  }

  /**
   * When the signed-in user's newest cache entry was written (what an offline
   * banner calls "saved data from"), or null with no user or nothing saved.
   * Only this user's entries from this build count.
   */
  savedAt(): number | null {
    const prefix = this.getCacheKey("");
    if (!prefix) return null;
    let newest: number | null = null;
    try {
      for (const key of allStorageKeys()) {
        if (!key.startsWith(prefix)) continue;
        try {
          const entry = JSON.parse(localStorage.getItem(key) || "null");
          if (!entry || entry.version !== CACHE_VERSION) continue;
          if (typeof entry.timestamp !== "number") continue;
          if (newest === null || entry.timestamp > newest) {
            newest = entry.timestamp;
          }
        } catch {
          // a corrupt entry is not saved data
        }
      }
    } catch {
      return null;
    }
    return newest;
  }
}

// Export singleton instance
export const cache = CacheManager.getInstance();

/**
 * Where the signed-in user's selected workspace is stored. Per user, so it
 * survives signing out and back in but never carries over to someone else.
 * With no known user it is the old unscoped key, which sign-out removes.
 */
export function selectedWorkspaceKey(): string {
  const user = cache.getUser();
  return user ? `selectedWorkspace:${scopeId(user)}` : "selectedWorkspace";
}

// Cache keys for different data types
export const CACHE_KEYS = {
  WORKSPACES: "workspaces",
  WORKSPACE_DETAILS: (id: number) => `workspace_${id}`,
  GROUPS: "groups",
  GROUP_DETAILS: (id: number) => `group_${id}`,
  LINKS: "links",
  GROUP_LINKS: (groupId: number) => `group_${groupId}_links`,
  NOTES: "notes",
  GROUP_NOTES: (groupId: number) => `group_${groupId}_notes`,
  REFERENCE_GUIDES: "reference_guides",
  USER_PREFERENCES: "user_preferences",
  WORKSPACE_PREFERENCES: (id: number) => `workspace_${id}_preferences`,
} as const;

// One in-flight fetch per cache key. Several callers mount at once on page
// load (ReduxInitializer plus the page's own effect), and without this each
// of them fired its own identical request.
//
// Each fetch gets a generation number. A forced refresh, or invalidating the
// key (cache.remove after a mutation), moves the generation on, so a request
// that started earlier can't write its now-stale response back: when it lands
// it hands its callers the newer request's result instead. Switching user
// moves the epoch on, which discards every request started for the previous
// user.
interface Flight {
  promise: Promise<unknown>;
  generation: number;
}
const inFlight = new Map<string, Flight>();
const generations = new Map<string, number>();
let epoch = 0;

/** Thrown to callers of a request started for a user who is no longer signed in. */
export class CacheUserChangedError extends Error {
  constructor() {
    super("The signed-in user changed while this request was in flight");
    this.name = "CacheUserChangedError";
  }
}

function nextGeneration(key: string): number {
  const generation = (generations.get(key) ?? 0) + 1;
  generations.set(key, generation);
  return generation;
}

function invalidateKey(key: string): void {
  nextGeneration(key);
  inFlight.delete(key);
}

function invalidatePrefix(prefix: string): void {
  for (const key of [...generations.keys(), ...inFlight.keys()]) {
    if (key.startsWith(prefix)) invalidateKey(key);
  }
}

function fetchAndStore<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttl: number | undefined,
  force: boolean,
): Promise<T> {
  const pending = inFlight.get(key);
  if (pending && !force) {
    return pending.promise as Promise<T>;
  }

  const generation = nextGeneration(key);
  const startEpoch = epoch;

  const request: Promise<T> = fetcher()
    .then((freshData): T | Promise<T> => {
      if (epoch !== startEpoch) throw new CacheUserChangedError();
      cache.recordApiSuccess();
      if (generations.get(key) !== generation) {
        // Superseded: a forced refresh or an invalidation came after this
        // request started. Follow the newer request, or start one.
        const newer = inFlight.get(key);
        return newer && newer.generation > generation
          ? (newer.promise as Promise<T>)
          : fetchAndStore(key, fetcher, ttl, false);
      }
      cache.set(key, freshData, { ttl });
      return freshData;
    })
    .catch((error) => {
      if (epoch === startEpoch && !(error instanceof CacheUserChangedError)) {
        cache.recordApiFailure(error);
      }
      throw error;
    })
    .finally(() => {
      if (inFlight.get(key)?.generation === generation) {
        inFlight.delete(key);
      }
    });

  inFlight.set(key, { promise: request, generation });
  return request;
}

/**
 * Stale-while-revalidate wrapper around an API fetch.
 *
 * - Any cached entry, fresh or expired, is returned immediately and a
 *   background refetch updates the cache (and calls onFreshData when the
 *   result differs). The TTL therefore controls when the cached copy is
 *   considered stale, never whether it is shown.
 * - Only a true miss (no entry at all) or forceRefresh awaits the network.
 * - If the awaited fetch fails and an entry exists, the entry is returned
 *   instead of throwing.
 */
export async function withCache<T>(
  key: string,
  fetcher: () => Promise<T>,
  options?: {
    ttl?: number;
    forceRefresh?: boolean;
    onCacheHit?: (data: T) => void;
    onCacheMiss?: () => void;
    onFreshData?: (data: T) => void;
  },
): Promise<T> {
  const { ttl, forceRefresh, onCacheHit, onCacheMiss, onFreshData } =
    options || {};

  const cached = forceRefresh ? null : cache.get<T>(key, { ttl: Infinity });

  if (cached !== null) {
    onCacheHit?.(cached);

    fetchAndStore(key, fetcher, ttl, false)
      .then((freshData) => {
        if (JSON.stringify(freshData) !== JSON.stringify(cached)) {
          onFreshData?.(freshData);
        }
      })
      .catch((error) => {
        logger.debug("Background cache refresh failed", {
          key,
          error: error.message,
        });
      });

    return cached;
  }

  onCacheMiss?.();

  try {
    return await fetchAndStore(key, fetcher, ttl, !!forceRefresh);
  } catch (error) {
    if (error instanceof CacheUserChangedError) throw error;
    const staleData = cache.get<T>(key, { ttl: Infinity });
    if (staleData !== null) {
      logger.warn("Using stale cache data due to fetch error", { key });
      return staleData;
    }
    throw error;
  }
}
